import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import api from "../../../api/api";
import toast from "react-hot-toast";
import "react-quill-new/dist/quill.snow.css";

const emptySeo = {
  meta_title: "",
  meta_description: "",
  focus_keyword: "",
  keywords: [],
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  no_index: false,
  no_follow: false,
};

const ProjectForm = ({ initialData, onSubmit, loading }) => {
  const [categories, setCategories] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    summary: "",
    content: "",
    category_id: "",
    featured_image: "",
    features: "",
    location: "",
    seo: emptySeo,
  });

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ],
    }),
    []
  );

  useEffect(() => {
    if (initialData) {
      setForm((prev) => ({
        ...prev,
        ...initialData,
        features: Array.isArray(initialData.features)
          ? initialData.features.join(", ")
          : initialData.features || "",
        seo: {
          ...emptySeo,
          ...(initialData.seo || {}),
          keywords: initialData.seo?.keywords || [],
        },
      }));
    }
  }, [initialData]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await api.get("/project-categories");
      setCategories(res.data || []);
    } catch (err) {
      toast.error(err.cleanMessage || "Failed to load project categories");
    }
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateSeo = (key, value) => {
    setForm((prev) => ({
      ...prev,
      seo: {
        ...prev.seo,
        [key]: value,
      },
    }));
  };

  const uploadFeatured = async (file) => {
    if (!file) return;

    setMediaLoading(true);

    try {
      const body = new FormData();
      body.append("file", file);

      const res = await api.post("/media/upload", body, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      updateForm("featured_image", res.data.url);
      toast.success("Project image uploaded");
    } catch (err) {
      toast.error(err.cleanMessage || "Image upload failed");
    } finally {
      setMediaLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error("Project title is required");
      return;
    }

    if (!form.category_id) {
      toast.error("Project category is required");
      return;
    }

    if (!form.content.trim()) {
      toast.error("Project details are required");
      return;
    }

    onSubmit({
      ...form,
      title: form.title.trim(),
      summary: form.summary.trim(),
      location: form.location.trim(),
      features: form.features
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      seo: {
        ...form.seo,
        keywords: form.seo.keywords,
      },
    });
  };

  return (
    <form onSubmit={submit} className="grid xl:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <label className="block mb-5">
            <span className="font-bold text-slate-700">Project Title</span>
            <input
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              required
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Enter project title"
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Short Summary</span>
            <textarea
              value={form.summary}
              onChange={(e) => updateForm("summary", e.target.value)}
              rows="3"
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Short project description for the project card"
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Location</span>
            <input
              value={form.location}
              onChange={(e) => updateForm("location", e.target.value)}
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Example: Etinan LGA, Akwa Ibom State"
            />
          </label>

          <label className="block">
            <span className="font-bold text-slate-700">Project Details</span>

            <div className="mt-2 bg-white rounded-2xl overflow-hidden border border-slate-200">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(value) => updateForm("content", value)}
                modules={quillModules}
                placeholder="Write full project details here..."
              />
            </div>
          </label>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-xl font-black text-[#0b6b1d] mb-4">
            SEO Settings
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <span className="font-bold text-sm">Meta Title</span>
              <input
                value={form.seo.meta_title}
                maxLength={60}
                onChange={(e) => updateSeo("meta_title", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="SEO title"
              />
              <small className="text-slate-500">
                {form.seo.meta_title.length}/60
              </small>
            </label>

            <label>
              <span className="font-bold text-sm">Focus Keyword</span>
              <input
                value={form.seo.focus_keyword}
                onChange={(e) => updateSeo("focus_keyword", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="Main keyword"
              />
            </label>

            <label className="md:col-span-2">
              <span className="font-bold text-sm">Meta Description</span>
              <textarea
                value={form.seo.meta_description}
                maxLength={160}
                onChange={(e) => updateSeo("meta_description", e.target.value)}
                rows="3"
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="SEO description"
              />
              <small className="text-slate-500">
                {form.seo.meta_description.length}/160
              </small>
            </label>

            <label>
              <span className="font-bold text-sm">Keywords</span>
              <input
                value={form.seo.keywords.join(", ")}
                onChange={(e) =>
                  updateSeo(
                    "keywords",
                    e.target.value
                      .split(",")
                      .map((keyword) => keyword.trim())
                      .filter(Boolean)
                  )
                }
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="school project, classroom, renovation"
              />
            </label>

            <label>
              <span className="font-bold text-sm">Canonical URL</span>
              <input
                value={form.seo.canonical_url}
                onChange={(e) => updateSeo("canonical_url", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="https://example.com/project-url"
              />
            </label>
          </div>
        </div>
      </div>

      <aside className="space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Save Project</h3>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Project"}
          </button>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Project Image</h3>

          {form.featured_image ? (
            <img
              src={form.featured_image}
              alt="Project"
              className="w-full h-44 object-cover rounded-2xl mb-4 border"
            />
          ) : (
            <div className="w-full h-44 rounded-2xl mb-4 border border-dashed flex items-center justify-center text-slate-400 text-sm">
              No project image
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => uploadFeatured(e.target.files?.[0])}
            className="w-full text-sm"
          />

          {mediaLoading && (
            <p className="text-sm mt-2 text-slate-500">Uploading...</p>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Project Category</h3>

          <select
            value={form.category_id}
            onChange={(e) => updateForm("category_id", e.target.value)}
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
          >
            <option value="">Select Category</option>

            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <p className="text-xs text-slate-500 mt-2">
            Examples: Completed, Ongoing, Planned.
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Card Features</h3>

          <input
            value={form.features}
            onChange={(e) => updateForm("features", e.target.value)}
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
            placeholder="Classrooms, Furniture, Water Facilities"
          />

          <p className="text-xs text-slate-500 mt-2">
            Separate each feature with commas.
          </p>
        </div>
      </aside>
    </form>
  );
};

export default ProjectForm;